const FFT_SIZE = 64;
const FREQUENCY_BIN_COUNT = FFT_SIZE / 2;

export class AudioAnalyserService {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private currentSource: AudioNode | null = null;
  private frequencyData: Uint8Array<ArrayBuffer> | null = null;

  private ensureContext(): boolean {
    if (this.audioContext) return true;

    try {
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = FFT_SIZE;
      this.analyser.smoothingTimeConstant = 0.6;
      this.frequencyData = new Uint8Array(FREQUENCY_BIN_COUNT);
      return true;
    } catch {
      return false;
    }
  }

  async connectMicStream(stream: MediaStream): Promise<void> {
    if (!this.ensureContext()) return;

    this.disconnectSource();
    await this.audioContext!.resume().catch(() => {
      /* AudioContext resume failed - audio may not work */
    });

    const source = this.audioContext!.createMediaStreamSource(stream);
    source.connect(this.analyser!);
    // Do NOT connect analyser to destination - avoids echo/feedback
    this.currentSource = source;
  }

  connectAudioElement(audio: HTMLAudioElement): void {
    if (!this.ensureContext()) return;

    this.disconnectSource();

    const source = this.audioContext!.createMediaElementSource(audio);
    source.connect(this.analyser!);
    this.analyser!.connect(this.audioContext!.destination);
    this.currentSource = source;
  }

  disconnect(): void {
    this.disconnectSource();
  }

  getAmplitude(): number {
    if (!this.analyser || !this.frequencyData) return 0;

    this.analyser.getByteFrequencyData(this.frequencyData);

    // RMS of frequency bins for a smooth amplitude reading
    let sum = 0;
    for (let i = 0; i < this.frequencyData.length; i++) {
      const normalized = this.frequencyData[i] / 255;
      sum += normalized * normalized;
    }
    return Math.sqrt(sum / this.frequencyData.length);
  }

  dispose(): void {
    this.disconnectSource();
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.analyser = null;
    this.frequencyData = null;
  }

  private disconnectSource(): void {
    if (this.currentSource) {
      try {
        this.currentSource.disconnect();
      } catch {
        // Already disconnected
      }
      this.currentSource = null;
    }
    // Disconnect analyser from destination (only matters for audio element path)
    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        // Already disconnected
      }
    }
  }
}
